import { createRouter, createWebHistory } from 'vue-router'
import Swal from 'sweetalert2'
import { useStore } from '@front/composables/store'
import { installSheets } from './lib/sheets'
import { rememberTabPath, rememberScroll, takeTabNavigation } from './lib/tabs'
import Home from './pages/Home.vue'
import checkoutRoutes from './routes/checkout'
import accountRoutes from './routes/account'
import contentRoutes from './routes/content'

const routes = [
	{ path: '/', component: Home, meta: { tab: 'home', keepAlive: true, title: 'In His Hands Birth Supply' } },
	{ path: '/shop', component: () => import('./pages/Shop.vue'), meta: { tab: 'shop', keepAlive: true, title: 'Shop | In His Hands Birth Supply' } },
	{ path: '/categories', redirect: '/shop' },
	{ path: '/categories/:path(.*)', component: () => import('./pages/Category.vue'), meta: { tab: 'shop', keepAlive: true } },
	{ path: '/products/:id', component: () => import('./pages/Product.vue'), meta: { tab: 'shop', tabBar: false, usePathKey: true } },
	{ path: '/item/:id', redirect: (to) => `/products/${to.params.id}` },
	{ path: '/search', component: () => import('./pages/Search.vue'), meta: { tab: 'shop', tabBar: false, title: 'Search | In His Hands Birth Supply' } },
	{ path: '/brands', component: () => import('./pages/Brands.vue'), meta: { tab: 'shop', keepAlive: true, title: 'Brands | In His Hands Birth Supply' } },
	{ path: '/brands/:id', component: () => import('./pages/Brand.vue'), meta: { tab: 'shop', keepAlive: true } },
	{ path: '/more', component: () => import('./pages/More.vue'), meta: { tab: 'more', title: 'More | In His Hands Birth Supply' } },
	...checkoutRoutes,
	...accountRoutes,
	// Content routes end with the catch-all (CMS pages, then 404), so they go last.
	...contentRoutes,
]

export function createAppRouter() {
	// Back/forward presses arrive as popstate before the router sees them.
	let poppedByUser = false
	window.addEventListener('popstate', () => {
		poppedByUser = true
		// The router's guards for this pop run before the next task; don't let the flag leak further.
		setTimeout(() => (poppedByUser = false), 0)
	})

	const router = createRouter({
		history: createWebHistory(),
		routes,
		scrollBehavior(to, from, savedPosition) {
			// Opening or closing a sheet changes only ?sheet= — keep the page where it is.
			if (to.path === from.path && to.query.sheet !== from.query.sheet) return false
			if (savedPosition) return savedPosition
			// Switching tabs returns to where you were in that tab.
			const tabScroll = takeTabNavigation(to)
			if (tabScroll != null) return { top: tabScroll }
			if (to.hash) return { el: to.hash }
			if (to.path === from.path) return false
			return { top: 0 }
		},
	})

	router.beforeEach(async (to) => {
		const wasPop = poppedByUser
		poppedByUser = false

		// Back closes an open alert first, like a sheet, instead of leaving the page.
		if (wasPop && Swal.isVisible()) {
			Swal.close()
			return false
		}

		const store = useStore()
		// Opened cold on an account page: let the saved session load before deciding.
		// Cookie sessions have no localStorage token. Wait for restore before sending
		// a shopper who is already signed in to the login page.
		if (to.meta.protected && !store.user?.id && store.userReady) await store.userReady
		if (to.meta.protected && !store.user?.id) {
			store.login = { next: to.fullPath }
			return { path: '/login', query: { next: to.fullPath } }
		}
		if (to.meta.adminOnly && !store.user?.roles?.includes('admin')) return '/more'
	})

	router.beforeEach((to, from) => {
		rememberScroll(from)
	})

	router.afterEach((to) => {
		if (to.meta.title) document.title = to.meta.title
		rememberTabPath(to, router)
	})

	installSheets(router)
	return router
}
